/**
 * THE FRAME EVERY SCREEN SITS IN — CTS 1's navy rail and its page head.
 *
 * The rail is drawn FROM `src/screens/contracts.ts`: the sections, which
 * screens they hold, and which are not built yet. A section whose screens do
 * not exist is drawn as "later" rather than hidden, so the shape of the
 * product is visible and nobody wonders whether billing was forgotten (the
 * contract file says why each one waits). A section is drawn at all only if
 * the user holds a function one of its screens needs — permission decides
 * what is offered, and the server decides again on every call.
 */
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { SCREENS, menuFor } from '../../src/screens/contracts.js';
import { Icon } from './icons.js';
import { can, type Session } from './api.js';
import { go } from './hooks.js';

/** 10 Oct — a real icon per place (ui/src/icons.tsx), one sprite, no font and no image request. */
const ICONS: Record<string, string> = {
  overview: 'today', sales: 'sales', ops: 'movements', fleet: 'cylinders', billing: 'money',
  purchasing: 'buying', insights: 'reports', masters: 'settings', administration: 'settings', settings: 'settings', filling: 'in',
};

/** CTS1's sidebar remembered which pillars were open (kig_nav_open); this one does too. */
function readNavOpen(): Record<string, boolean> {
  try { return JSON.parse(globalThis.localStorage?.getItem('cts2_nav_open') ?? '{}') as Record<string, boolean>; } catch { return {}; }
}
function childLabel(screen: { navLabel?: string; title: string }): string {
  return screen.navLabel ?? screen.title.split(' — ')[0] ?? screen.title;
}

/**
 * THE SEARCH THAT IS ALWAYS THERE.
 *
 * It sits above the sections because it is used more than any of them: a
 * person with a challan number in his hand should not have to work out which
 * module it lives in first. `/` focuses it from anywhere, the way it does in
 * every tool the yard already uses.
 */
function RailSearch() {
  const [q, setQ] = useState('');
  const box = useRef<{ focus: () => void; select: () => void } | null>(null);

  useEffect(() => {
    const onKey = (e: { key: string; target: unknown; preventDefault: () => void }) => {
      const t = e.target as { tagName?: string } | null;
      const typing = t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA' || t?.tagName === 'SELECT';
      if (e.key === '/' && !typing) { e.preventDefault(); box.current?.focus(); box.current?.select(); }
    };
    globalThis.addEventListener?.('keydown', onKey as never);
    return () => globalThis.removeEventListener?.('keydown', onKey as never);
  }, []);

  return (
    <form className="rail-search" role="search"
          onSubmit={(e: { preventDefault: () => void }) => { e.preventDefault(); if (q.trim()) { go(`#/find?q=${encodeURIComponent(q.trim())}`); setQ(''); } }}>
      <label className="sr-only" htmlFor="rail-find">Find a cylinder, challan, order or customer</label>
      <input id="rail-find" type="search" value={q} placeholder="Find  ·  press /" autoComplete="off"
             ref={box as never}
             onChange={(e: { target: { value: string } }) => setQ(e.target.value)} />
    </form>
  );
}

export function Rail({ session, active, onSignOut, onPalette, onBranch, branchError }: {
  session: Session; active: string; onSignOut: () => void; onPalette?: () => void;
  /** 0121 — the person says which yard they are at. Absent means the picker is not offered. */
  onBranch?: (branchId: number | null) => void;
  /**
   * 0121 — why the last attempt to choose a yard was refused, in the door's own words.
   *
   * It used to be swallowed: a clerk choosing a yard that had just been closed, or one no
   * longer granted to them, saw the picker not move and no reason why. The sentence names
   * the yard and says what to do, and nothing else can.
   */
  branchError?: { title: string; detail?: string } | null;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>(readNavOpen);
  const toggle = (id: string) => setOpen((o) => {
    const next = { ...o, [id]: !(o[id] ?? true) };
    try { globalThis.localStorage?.setItem('cts2_nav_open', JSON.stringify(next)); } catch { /* a private window forgets; the rail still works */ }
    return next;
  });
  const visible = menuFor(session.roles ?? []).filter((section) => {
    if (!section.built) return true;                       // shown as "later"
    return section.screens.some((id) => {
      const screen = SCREENS.find((s) => s.id === id);
      return screen ? can(session, screen.requires) : false;
    });
  });

  return (
    <aside className="rail">
      <div className="rail-head">
        {session.company?.markSvg
          /* The mark is the company's own SVG, held to 64 kB and refused if it carries
             script or an external reference (0177). It is drawn through a data: URI in an
             <img>, so nothing in it can run against this page. */
          ? <img className="rail-logo" alt={session.company.tradingName || 'the company'}
                 src={`data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(session.company.markSvg)))}`} />
          : <span className="rail-mark">{session.company?.code || 'CTS'}</span>}
        <span className="rail-who">
          <strong>{session.company?.displayName ?? `User ${session.actorId}`}</strong>
          <span>{session.company?.tradingName ?? 'Signed in'}</span>
        </span>
      </div>
      {onPalette ? (
        <button type="button" className="rail-k" onClick={onPalette}>
          <span>What do you want to do?</span>
          <kbd>⌘K</kbd>
        </button>
      ) : null}
      <RailSearch />
      <nav aria-label="Sections">
        {visible.map((section) => {
          if (!section.built) {
            return (
              <div className="nav-later" key={section.id} title={section.note ?? ''}>
                <span><Icon name={ICONS[section.id] ?? 'today'} /> {section.label}</span><em>later</em>
              </div>
            );
          }
          const children = section.screens
            .map((id) => SCREENS.find((s) => s.id === id))
            .filter((s): s is NonNullable<typeof s> => !!s && can(session, s.requires));
          const first = children[0]?.id;
          const isActive = section.screens.includes(active);
          if (children.length <= 1) {
            return (
              <a className="nav-item" key={section.id} href={`#/${first ?? 'home'}`}
                 aria-current={isActive ? 'page' : undefined}
                 onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/${first ?? 'home'}`); }}>
                <Icon name={ICONS[section.id] ?? 'today'} />
                <span>{section.label}</span>
              </a>
            );
          }
          // A pillar is never a dead header: clicking it goes to its first
          // child AND opens it; only the chevron toggles without going
          // anywhere (CTS1, App.jsx:3378).
          // 10 Oct — a place opens when you are in it; the others stay folded so the eight
          // places fit a laptop screen. A person may still pin one open with the chevron.
          const isOpen = open[section.id] ?? true;
          return (
            <div className="nav-pillar" key={section.id}>
              <div className="nav-pillar-row">
                <a className="nav-item" href={`#/${first ?? 'home'}`}
                   aria-current={isActive && !isOpen ? 'page' : undefined}
                   onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); if (!isOpen) toggle(section.id); go(`#/${first ?? 'home'}`); }}>
                  <Icon name={ICONS[section.id] ?? 'today'} />
                  <span>{section.label}</span>
                </a>
                <button type="button" className="nav-chevron" aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${section.label}`}
                        onClick={() => toggle(section.id)}><Icon name="chevron" size={14} className={isOpen ? 'turn' : ''} /></button>
              </div>
              {isOpen ? children.map((s) => (
                <a className="nav-item nav-child" key={s.id} href={`#/${s.id}`}
                   aria-current={active === s.id ? 'page' : undefined}
                   onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/${s.id}`); }}>
                  <span>{childLabel(s)}</span>
                </a>
              )) : null}
            </div>
          );
        })}
      </nav>
      <div className="rail-foot">
        {/*
          * 0121 — WHICH YARD, shown only when there is a choice to make.
          *
          * One branch, or none, and this is absent: a picker with one option is noise, and
          * a company that has not adopted branches should see no sign of them at all. The
          * value sits in the rail rather than on a settings page because it changes what
          * every form records, and a person needs to see it without going to look for it.
          */}
        {onBranch && session.branches.length > 1 ? (
          <label className="rail-branch">
            <span className="muted">Working at</span>
            <select value={session.activeBranchId === null ? '' : String(session.activeBranchId)}
                    onChange={(e: { currentTarget: { value: string } }) =>
                      onBranch(e.currentTarget.value === '' ? null : Number(e.currentTarget.value))}>
              <option value="">
                {`my usual yard${session.branches.find((b) => b.isDefault) ? ` (${session.branches.find((b) => b.isDefault)!.code})` : ''}`}
              </option>
              {session.branches.map((b) => (
                <option key={b.branchId} value={String(b.branchId)}>{`${b.code} — ${b.name}`}</option>
              ))}
            </select>
            {branchError
              ? <span className="rail-branch-error" role="alert">
                  {branchError.title}
                  {branchError.detail ? <span className="muted">{` ${branchError.detail}`}</span> : null}
                </span>
              : null}
          </label>
        ) : null}
        <button className="btn ghost wide" onClick={onSignOut}>Sign out</button>
        {/*
          WHICH BUILD IS THIS? The first thing worth asking for when somebody
          reports a fault. The version is the RELEASE a person reads — not a
          migration number, and not the RELEASE file's build stamp, which is
          the right identifier for support and the wrong one to read out over
          a phone. A trailing + means the tree was not clean.
        */}
        <div className="rail-build" title="The release version and the exact commit it was built from">
          CTS v{typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '?'}
          <span>build {typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__ : 'unknown'}</span>
        </div>
      </div>
    </aside>
  );
}

/**
 * 10 Oct — THE PHONE'S BAR. On a narrow screen the rail folds into a row that scrolls off the
 * edge; the yard needs the eight places under a thumb. Same sections, same permission filter,
 * different classes so the rail's own tests stay honest about the rail.
 */
export function BottomBar({ session, active }: { session: Session; active: string }) {
  // Rendered only on a narrow screen. Hiding it with CSS alone left eight anchors in the DOM
  // whose text a desktop click-by-text could land on (the walk found it: a hidden link
  // swallowed the click and the queue never opened).
  const [narrow, setNarrow] = useState<boolean>(() => typeof globalThis.matchMedia === 'function' && globalThis.matchMedia('(max-width: 900px)').matches);
  useEffect(() => {
    if (typeof globalThis.matchMedia !== 'function') return;
    const mq = globalThis.matchMedia('(max-width: 900px)');
    const on = () => setNarrow(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  if (!narrow) return null;
  const places = menuFor(session.roles ?? []).filter((section) => section.built && section.screens.some((id) => {
    const s = SCREENS.find((x) => x.id === id); return !!s && can(session, s.requires);
  }));
  return (
    <nav className="bottom-bar" aria-label="Places">
      {places.map((section) => {
        const first = section.screens.find((id) => { const s = SCREENS.find((x) => x.id === id); return !!s && can(session, s.requires); }) ?? section.screens[0];
        const isActive = section.screens.includes(active);
        return (
          <a key={section.id} href={`#/${first}`} className={`bottom-item${isActive ? ' on' : ''}`} aria-current={isActive ? 'page' : undefined}
             onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/${first}`); }}>
            <Icon name={ICONS[section.id] ?? 'today'} size={20} />
            <span>{section.label}</span>
          </a>
        );
      })}
    </nav>
  );
}

export function Page({ title, sub, actions, children }: {
  title: string; sub?: string; actions?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <main className="main" id="content">
      <header className="page-head">
        <div>
          <h1>{title}</h1>
          {sub ? <p>{sub}</p> : null}
        </div>
        {actions ? <div className="page-actions">{actions}</div> : null}
      </header>
      {children}
    </main>
  );
}

/** The screen a person reaches without the function it needs. */
export function NotPermitted({ fn }: { fn: string }) {
  return (
    <Page title="Not yours to open" sub={`This screen needs the function ${fn}.`}>
      <div className="card"><div className="card-body">
        <p>Ask a manager to grant <code>{fn}</code>. Nothing on this screen is hidden from you by accident — the
           system shows what your functions allow and asks the server again every time.</p>
      </div></div>
    </Page>
  );
}
